import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  ClipboardList,
  Calendar,
  Compass,
  Users,
  MapPin,
  Dices,
  Scroll,
} from 'lucide-react';

const QUEST_STATUS_COLORS = {
  known: 'bg-blue-500/10 text-blue-600',
  in_progress: 'bg-yellow-500/10 text-yellow-600',
};

const NPC_STATUS_COLORS = {
  alive: 'text-green-600',
  dead: 'text-red-600',
  missing: 'text-muted-foreground',
  hostile: 'text-red-500',
  ally: 'text-blue-500',
};

export const PlayerDashboardView = ({ campaignId }) => {
  const { language } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchDashboard = async () => {
      try {
        const res = await axios.get(`/api/campaigns/${campaignId}/player-dashboard`);
        if (!cancelled) setData(res.data);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDashboard();
    return () => { cancelled = true; };
  }, [campaignId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse text-muted-foreground">{t('loading', language)}</div>
      </div>
    );
  }

  if (!data) return null;

  const sheet = data.character_sheet;

  return (
    <div className="space-y-6">
      {/* Character card */}
      {sheet && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 font-cinzel text-lg">
              <ClipboardList className="h-5 w-5 text-primary" />
              {language === 'fr' ? 'Mon Personnage' : 'My Character'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Link
              to={`/campaigns/${campaignId}/character-sheet`}
              className="block hover:bg-accent/50 rounded-lg p-3 -m-3 transition-colors"
            >
              <p className="font-semibold text-lg">
                {sheet.data?.name || sheet.data?.character_name || (language === 'fr' ? 'Personnage sans nom' : 'Unnamed character')}
              </p>
              {(sheet.data?.class || sheet.data?.race) && (
                <p className="text-sm text-muted-foreground">
                  {[sheet.data.race, sheet.data.class, sheet.data.level && `Niv. ${sheet.data.level}`].filter(Boolean).join(' · ')}
                </p>
              )}
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Upcoming sessions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {language === 'fr' ? 'Prochaines Sessions' : 'Upcoming Sessions'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.upcoming_sessions || data.upcoming_sessions.length === 0) ? (
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Aucune session planifiée' : 'No sessions scheduled'}</p>
            ) : (
              <div className="space-y-2">
                {data.upcoming_sessions.map(s => (
                  <div key={s.id} className="flex items-center justify-between text-sm">
                    <span className="truncate">{s.title}</span>
                    {s.scheduled_date && (
                      <span className="text-xs text-muted-foreground shrink-0 ml-2">
                        {new Date(s.scheduled_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active quests */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Compass className="h-4 w-4 text-muted-foreground" />
              {language === 'fr' ? 'Quêtes Actives' : 'Active Quests'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.active_quests || data.active_quests.length === 0) ? (
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Aucune quête connue' : 'No known quests'}</p>
            ) : (
              <div className="space-y-2">
                {data.active_quests.map(q => (
                  <Link
                    key={q.id}
                    to={`/campaigns/${campaignId}/quests/${q.id}`}
                    className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                  >
                    <span className="truncate flex-1">{q.title}</span>
                    <Badge variant="outline" className={`text-[10px] ${QUEST_STATUS_COLORS[q.status] || ''}`}>
                      {q.status}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Known NPCs */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Users className="h-4 w-4 text-muted-foreground" />
              {language === 'fr' ? 'PNJ Rencontrés' : 'Known NPCs'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.known_npcs || data.known_npcs.length === 0) ? (
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Aucun PNJ connu' : 'No known NPCs'}</p>
            ) : (
              <div className="space-y-1.5">
                {data.known_npcs.slice(0, 8).map(npc => (
                  <Link
                    key={npc.id}
                    to={`/campaigns/${campaignId}/npcs/${npc.id}`}
                    className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                  >
                    <span className="truncate">{npc.name}</span>
                    {npc.faction && <span className="text-xs text-muted-foreground">({npc.faction})</span>}
                    {npc.status && (
                      <span className={`text-xs ml-auto ${NPC_STATUS_COLORS[npc.status] || 'text-muted-foreground'}`}>
                        {npc.status}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent published sessions (recaps) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Scroll className="h-4 w-4 text-muted-foreground" />
              {language === 'fr' ? 'Récaps de Session' : 'Session Recaps'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.published_sessions || data.published_sessions.length === 0) ? (
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Aucun récap publié' : 'No recaps published'}</p>
            ) : (
              <div className="space-y-2">
                {data.published_sessions.slice(0, 5).map(s => (
                  <Link
                    key={s.id}
                    to={`/campaigns/${campaignId}/sessions/${s.id}`}
                    className="block text-sm hover:text-primary transition-colors"
                  >
                    <span className="truncate">{s.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent dice rolls */}
      {data.recent_rolls && data.recent_rolls.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Dices className="h-4 w-4 text-muted-foreground" />
              {language === 'fr' ? 'Derniers Jets de Dés' : 'Recent Dice Rolls'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {data.recent_rolls.slice(0, 5).map(roll => (
                <div key={roll.id} className="flex items-center gap-3 text-sm">
                  <span className="text-xs text-muted-foreground w-16 truncate">{roll.username}</span>
                  <span className="text-xs text-muted-foreground">{roll.notation}</span>
                  <span className="ml-auto font-bold tabular-nums">{roll.total}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PlayerDashboardView;
